import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import PropTypes from 'prop-types';

import { useViewTracking } from 'hooks';

// Components
import { Mui, MuiLab } from 'components';

const useStyles = Mui.makeStyles(theme => ({
  actions: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(2),
    },
  },
  alert: {
    padding: theme.spacing(1, 3),
    minHeight: 64,
    display: 'flex',
    alignItems: 'center',
    borderRadius: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    '& .MuiAlert-message': {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  },
}));

function AlertItem({
  type,
  slug,
  alert,
  isFirst,
  hasMore,
  expanded,
  onToggleExpand,
  onInteraction,
  totalRest,
}) {
  // Hooks
  const classes = useStyles();
  const history = useHistory();

  const elementRef = useViewTracking(alert?.hasViewed, () => onInteraction(alert?.uid, 'view'), {
    threshold: 0.8,
    delay: 2000,
  });

  // Actions
  const handleClick = useCallback(
    url => {
      if (!alert?.hasClicked) onInteraction(alert?.uid, 'click');
      const hasUrl = !!url && typeof url === 'string';
      if (hasUrl && url.startsWith('http')) window.open(url, '_blank', 'noopener,noreferrer');
      if (hasUrl && url.startsWith('/')) history.push(`/${type}/${slug}${url}`);
    },
    [alert?.uid, onInteraction, type, slug, history, alert?.hasClicked],
  );

  return (
    <MuiLab.Alert
      ref={elementRef}
      severity={alert?.severity}
      icon={false}
      className={classes.alert}>
      <Mui.Typography variant="body2" component="span" style={{ flex: 1, whiteSpace: 'pre-line' }}>
        {alert?.content}
      </Mui.Typography>
      <div className={classes.actions}>
        {alert?.ctaText && alert?.ctaHref && (
          <Mui.Button
            size="small"
            color="inherit"
            variant="outlined"
            onClick={() => handleClick(alert?.ctaHref)}>
            {alert?.ctaText}
          </Mui.Button>
        )}
        {isFirst && hasMore && (
          <Mui.Button
            size="small"
            variant="text"
            color="inherit"
            onClick={onToggleExpand}
            endIcon={
              <Mui.Icon fontSize="small">{expanded ? 'expand_less' : 'expand_more'}</Mui.Icon>
            }>
            {expanded ? 'Ver menos' : `+${totalRest} ${totalRest === 1 ? 'alerta' : 'alertas'}`}
          </Mui.Button>
        )}
      </div>
    </MuiLab.Alert>
  );
}

AlertItem.propTypes = {
  type: PropTypes.string,
  slug: PropTypes.string,
  alert: PropTypes.object.isRequired,
  isFirst: PropTypes.bool,
  hasMore: PropTypes.bool,
  expanded: PropTypes.bool,
  onToggleExpand: PropTypes.func,
  onInteraction: PropTypes.func.isRequired,
  totalRest: PropTypes.number,
};

export default AlertItem;
